/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, LogOut, ChevronDown, User } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '@store/auth.store';
import { useViewModeStore, type ViewMode } from '@store/viewMode.store';
import { useAuth } from '@hooks/useAuth';
import { useNotifications, type NotificationsView } from '@hooks/useNotifications';
import NotificationsPanel from './NotificationsPanel';
import HeaderSearch from './HeaderSearch';
import Avatar from '@components/ui/Avatar';
import Badge from '@components/ui/Badge';
import { formatRole } from '@/lib/roles';
import { ENV_LABEL, ORG_DISPLAY_NAME } from '@/lib/branding';
import { useResolvedPageTitle } from '@/lib/pageTitle';
/* ── Sub-components ───────────────────────────────────────────────────── */

function UserMenuDropdown({
  user,
  canToggleMode,
  effectiveMode,
  onSwitchMode,
  onClose,
}: {
  user: { fullName?: string; role?: string } | null;
  canToggleMode: boolean;
  effectiveMode: ViewMode;
  onSwitchMode: (mode: ViewMode) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.15 }}
      className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-lg border border-surface-border overflow-hidden z-50"
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-border">
        <Avatar name={user?.fullName} size="sm" />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-text-primary">{user?.fullName}</span>
          {user?.role && <span className="text-xs text-text-muted">{formatRole(user.role)}</span>}
        </div>
      </div>
      {/*
        The narrow-viewport home for the view switch. `ViewModeSwitch` in the
        header is `sm:flex`, so without this row an admin below 640px would
        have no way to reach Learner view at all: this control used to live
        here unconditionally, and moving it to the header would have deleted
        the capability on phones rather than relocating it. Exactly one of the
        two is reachable at any width, and both drive the same store.
      */}
      {canToggleMode && (
        <div className="flex items-center justify-between border-b border-surface-border px-4 py-3 sm:hidden">
          <span className="text-sm font-medium text-text-primary">Admin view</span>
          <button
            type="button"
            role="switch"
            aria-checked={effectiveMode === 'manage'}
            aria-label="Admin view"
            onClick={() => onSwitchMode(effectiveMode === 'manage' ? 'learn' : 'manage')}
            className={clsx(
              'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
              effectiveMode === 'manage' ? 'bg-primary' : 'bg-surface-border'
            )}
          >
            <span
              className={clsx(
                'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                effectiveMode === 'manage' ? 'translate-x-[18px]' : 'translate-x-0.5'
              )}
            />
          </button>
        </div>
      )}
      <div className="py-1">
        <button
          onClick={() => {
            onClose();
            navigate('/learn/profile');
          }}
          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-text-primary hover:bg-surface-hover transition-colors"
        >
          <User size={15} aria-hidden="true" className="text-text-muted" /> Profile
        </button>
        <button
          onClick={() => {
            onClose();
            logout();
          }}
          className="mt-1 flex w-full items-center gap-3 border-t border-surface-border px-4 py-2.5 text-sm text-destructive transition-colors hover:bg-destructive/5"
        >
          <LogOut size={15} aria-hidden="true" /> Log out
        </button>
      </div>
    </motion.div>
  );
}

/* ── Component ────────────────────────────────────────────────────────── */

interface HeaderProps {
  /** Optional element rendered to the left of the page title (e.g. a back button). */
  leftSlot?: React.ReactNode;
}

function HeaderLeft({
  leftSlot,
  pageTitle,
  isLearnerView,
  showSearch = true,
}: {
  leftSlot?: React.ReactNode;
  pageTitle: string;
  isLearnerView: boolean;
  showSearch?: boolean;
}) {
  return (
    <div className="flex items-center gap-5 min-w-0 flex-1">
      {leftSlot}
      <h1 className="text-base font-medium text-foreground whitespace-nowrap">{pageTitle}</h1>
      {showSearch && <HeaderSearch isLearnerView={isLearnerView} />}
    </div>
  );
}

/** Exported for the a11y spec: the unread-count label is the point of it. */
export function NotificationsBell({
  notifications,
  showNotifs,
  panelRef,
  onBellClick,
  onCloseNotifs,
}: {
  /** The bell and its panel read one list — see `hooks/useNotifications`. */
  notifications: NotificationsView;
  showNotifs: boolean;
  panelRef: React.RefObject<HTMLDivElement>;
  onBellClick: () => void;
  onCloseNotifs: () => void;
}) {
  const { unreadCount } = notifications;
  // The count belongs in the name, not only in the badge: the badge is a
  // decorative glyph, so "Notifications" alone told a screen reader user
  // nothing about whether there was anything to look at.
  const label =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications, none unread';
  return (
    <div ref={panelRef} className="relative">
      <button
        type="button"
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
        onClick={onBellClick}
        title="Notifications"
        aria-label={label}
        aria-expanded={showNotifs}
      >
        <Bell size={18} aria-hidden="true" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              /* Offsets are measured from the button box, which now has p-2
                 (8px); 8px - 4px keeps the badge on the glyph's corner. */
              aria-hidden="true"
              className="absolute top-1 right-1 min-w-[16px] h-4 bg-destructive rounded-full flex items-center justify-center text-[9px] font-bold text-white px-0.5"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>
      {showNotifs && <NotificationsPanel notifications={notifications} onClose={onCloseNotifs} />}
    </div>
  );
}

/** Named after whose account it opens, so it is not just "button". */
export function accountMenuLabel(user: { fullName?: string } | null): string {
  return user?.fullName ? `Account menu for ${user.fullName}` : 'Account menu';
}

/**
 * Admin / Learner view switch.
 *
 * This used to be a lone toggle inside the account menu, which hid the control
 * that swaps the ENTIRE left-hand navigation behind a click on an avatar. It is
 * a segmented control in the header now, so the current view is readable
 * without opening anything.
 *
 * Selection is carried three ways on purpose. The white pill measures 1.12:1
 * against its own track, nowhere near the 3:1 a state indicator needs, so it
 * cannot be the signal by itself: the active option is also `aria-pressed`,
 * semibold, and near-black where the inactive one is blue. The pill is
 * reinforcement, not the message.
 */
function ViewModeSwitch({
  mode,
  onSwitchMode,
}: {
  mode: ViewMode;
  onSwitchMode: (mode: ViewMode) => void;
}) {
  const options: Array<{ value: ViewMode; label: string }> = [
    { value: 'manage', label: 'Admin' },
    { value: 'learn', label: 'Learner' },
  ];

  return (
    <div
      role="group"
      aria-label="View mode"
      className="hidden items-center gap-0.5 rounded-lg bg-[#eef2f9] p-0.5 sm:flex"
    >
      {options.map((option) => {
        const active = mode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (!active) onSwitchMode(option.value);
            }}
            className={clsx(
              'rounded-[7px] px-3 py-1.5 text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
              active
                ? 'bg-white font-semibold text-foreground shadow-sm'
                : 'font-medium text-primary hover:text-primary-700'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function HeaderRight({
  user,
  notifications,
  showNotifs,
  showUserMenu,
  canToggleMode,
  effectiveMode,
  orgDisplayName,
  panelRef,
  userMenuRef,
  onBellClick,
  onCloseNotifs,
  onSwitchMode,
  onCloseUserMenu,
  onToggleUserMenu,
}: {
  user: { fullName?: string; role?: string; id?: string } | null;
  notifications: NotificationsView;
  showNotifs: boolean;
  showUserMenu: boolean;
  canToggleMode: boolean;
  effectiveMode: ViewMode;
  /** `null` when unconfigured — the label is then omitted, not defaulted. */
  orgDisplayName: string | null;
  panelRef: React.RefObject<HTMLDivElement>;
  userMenuRef: React.RefObject<HTMLDivElement>;
  onBellClick: () => void;
  onCloseNotifs: () => void;
  onSwitchMode: (mode: ViewMode) => void;
  onCloseUserMenu: () => void;
  onToggleUserMenu: () => void;
}) {
  return (
    <div className="flex items-center gap-4">
      {canToggleMode && <ViewModeSwitch mode={effectiveMode} onSwitchMode={onSwitchMode} />}

      <NotificationsBell
        notifications={notifications}
        showNotifs={showNotifs}
        panelRef={panelRef}
        onBellClick={onBellClick}
        onCloseNotifs={onCloseNotifs}
      />

      {/*
        Organisation plus deployment environment, the same pairing the portal
        header uses (its HeaderTenantBadge: tenant in plain text, environment as
        a green success badge).

        role="status" on the wrapper with one aria-label, and aria-hidden on both
        children: assistive tech reads "Amida, environment dev" as a single
        phrase rather than two orphaned fragments. Copied from the portal
        deliberately, so the two products announce this the same way.
      */}
      {orgDisplayName && (
        <div
          role="status"
          aria-label={`${orgDisplayName}, environment ${ENV_LABEL}`}
          className="hidden items-center gap-2 whitespace-nowrap border-l border-border pl-6 md:flex"
        >
          <span className="text-sm text-muted-foreground" aria-hidden="true">
            {orgDisplayName}
          </span>
          <Badge variant="success" size="sm" aria-hidden="true">
            {ENV_LABEL}
          </Badge>
        </div>
      )}

      <div ref={userMenuRef} className="relative">
        <button
          type="button"
          onClick={onToggleUserMenu}
          /* Padding, hover and a focus ring so this reads as a control. It
             opens a menu, but with no border, no background and no hover state
             it looked like a label of who you are signed in as. */
          className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          aria-label={accountMenuLabel(user)}
          aria-expanded={showUserMenu}
        >
          <Avatar name={user?.fullName} size="sm" />
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-sm font-medium text-foreground">{user?.fullName}</span>
            {user?.role && (
              <span className="text-xs text-muted-foreground">{formatRole(user.role)}</span>
            )}
          </div>
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`text-muted-foreground/70 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
          />
        </button>
        <AnimatePresence>
          {showUserMenu && (
            <UserMenuDropdown
              user={user}
              canToggleMode={canToggleMode}
              effectiveMode={effectiveMode}
              onSwitchMode={onSwitchMode}
              onClose={onCloseUserMenu}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Header({ leftSlot }: HeaderProps = {}) {
  const { user } = useAuthStore();
  const { mode: viewMode, setMode: setViewMode } = useViewModeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const canToggleMode = !!user?.role && user.role !== 'learner';
  const effectiveMode: ViewMode = canToggleMode ? viewMode : 'learn';

  /*
   * One fetch of /badges/me + /certificates/me for the whole header
   * (duplicate-logic finding #27). The bell's unread count and the panel's list
   * are the same data now, so opening the bell costs no requests.
   *
   * The count is still the one place a failure legitimately degrades rather
   * than showing an error: there is nowhere on a 40px badge to put one, and the
   * panel it opens carries the real "could not load" state with a retry. The
   * failure is logged rather than erased (`loadList` -> clientLog).
   */
  const notifications = useNotifications(user?.id);

  const handleSwitchMode = (next: ViewMode) => {
    setViewMode(next);
    setShowUserMenu(false);
    navigate(next === 'learn' ? '/learn/dashboard' : '/dashboard');
  };

  useEffect(() => {
    if (!showNotifs && !showUserMenu) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setShowNotifs(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node))
        setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showNotifs, showUserMenu]);

  const handleBellClick = () => {
    const opening = !showNotifs;
    setShowNotifs(opening);
    // Opening the bell stamps lms_notifs_seen and zeroes the count. That is
    // intentional, not a bug — see `markSeen` in hooks/useNotifications.
    if (opening) notifications.markSeen();
  };

  // Accessibility survey #12: this used to be an exact-path lookup, so every
  // route carrying an id rendered <h1></h1>. `useResolvedPageTitle` prefers the
  // name the page published for itself and falls back to a route-pattern match
  // that is never empty.
  const pageTitle = useResolvedPageTitle();
  const isLearnerView = viewMode === 'learn' || user?.role === 'learner';
  // Hide the header search on Chat Studio, which has its own thread search in
  // the rail.
  const showSearch = !location.pathname.startsWith('/chat');

  return (
    <header className="flex items-center justify-between gap-6 h-[72px] px-6 bg-white border-b border-border shrink-0 sticky top-0 z-30">
      <HeaderLeft
        leftSlot={leftSlot}
        pageTitle={pageTitle}
        isLearnerView={isLearnerView}
        showSearch={showSearch}
      />
      <HeaderRight
        user={user}
        notifications={notifications}
        showNotifs={showNotifs}
        showUserMenu={showUserMenu}
        canToggleMode={canToggleMode}
        effectiveMode={effectiveMode}
        orgDisplayName={ORG_DISPLAY_NAME}
        panelRef={panelRef}
        userMenuRef={userMenuRef}
        onBellClick={handleBellClick}
        onCloseNotifs={() => setShowNotifs(false)}
        onSwitchMode={handleSwitchMode}
        onCloseUserMenu={() => setShowUserMenu(false)}
        onToggleUserMenu={() => setShowUserMenu((v) => !v)}
      />
    </header>
  );
}
